import React, { useRef, useState, ChangeEvent, useCallback, useEffect } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { styled } from "@superset-ui/core";
import { Input } from "src/components/Input";
import ControlHeader from "../../../ControlHeader";

export interface MapUploadControlProps {
    label: string;
    accept?: string[];
    placeholder?: string;
    onChange: (fileStr: string) => void;
    value?: string;
    actions: any;
}

interface FileData {
    file: string | ArrayBuffer | null;
    fileName: string;
    fileSize: number;
    fileType: string;
}

const CustomInput = styled(Input)`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding-right: 40px;
`;

const CustomUploadButton = styled(UploadOutlined)`
    position: absolute;
    right: 16px;
    top: 46%;
    transform: translate(50%, 50%);
    cursor: pointer;
`;

const MapUploadControl = (props: MapUploadControlProps) => {
    const { label, accept, placeholder, onChange, value, actions } = props;
    const fileRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string>("");

    useEffect(() => {
        if (value) {
            const parseJsonFileData = JSON.parse(value);
            setFileName(parseJsonFileData.fileName);
        }
    }, [value]);

    /**
     * @name handleUploadClick
     * @function
     * @description 업로드 버튼 클릭 이벤트 핸들러
     * @return {void}
     */
    const handleUploadClick = useCallback((): void => {
        fileRef.current?.click();
    }, []);

    /**
     * @name handleFileInputChange
     * @function
     * @description 파일 데이터 변경 이벤트 핸들러
     * @param {ChangeEvent<HTMLInputElement>} e
     * @return {void}
     */
    const handleFileInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const file = e.target.files[0];
                setFileName(file.name);
                actions.setControlValue("label_extend", []);

                const fileData: FileData = {
                    file: null,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                };

                const reader = new FileReader();

                if (file) {
                    reader.onloadend = () => {
                        fileData.file = reader.result;
                        const filePayload = JSON.stringify(fileData);
                        onChange(filePayload as string);
                    };
                    reader.readAsText(file);
                }
            }
        },
        [actions, onChange]
    );

    return (
        <div style={{ position: "relative" }}>
            <ControlHeader {...props} />
            <CustomInput placeholder={placeholder} value={fileName} aria-label={label} />
            <CustomUploadButton size={12} onClick={handleUploadClick} />
            <input
                type="file"
                accept={accept?.toString()}
                ref={fileRef}
                style={{ display: "none" }}
                onChange={handleFileInputChange}
            />
        </div>
    );
};

export default MapUploadControl;
